/**
 * two-column-media.js
 */

class TwoColMedia extends HTMLElement {
  constructor() {
    super();
    this._videoObserver = null;
    this._video = null;
    this._soundBtn = null;
    this._cta = null;
    this._gsapTimeline = null;
    this._soundTrigger = null;
    this._gestureUnlockHandler = null;
    this._onFirstScroll = null;
    this._audioUnlocked = false;
    this._isInView = false;
    this._userToggledSound = false;
    this._scrolled = false;
  }

  connectedCallback() {
    this._wrap = this.querySelector('[data-tcm-reveal]');
    this._cta = this.querySelector('[data-tcm-reveal-cta]');

    this._init();

    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.contains(this)) { this._teardown(); this._init(); }
    });
  }

  disconnectedCallback() { this._teardown(); }

  _init() {
    if (!this._wrap) return;

    this._onExternalPlay = (e) => {
      if (e.detail.source === this) return;
      this._pauseAndSilence();
    };
    document.addEventListener('video-section:play', this._onExternalPlay);

    this._initVideo();

    const isMobile = window.innerWidth < 1024;

    if (this.dataset.tcmMode === 'sticky' && isMobile) {
      this.dataset.tcmMode = 'natural';
    }

    if (this.dataset.tcmMode === 'sticky') {
      this._initStickyReveal();
    } else {
      this._initNaturalReveal(isMobile);
    }
  }

  _handleStickySound(isActive) {
    if (!this._video || !this._scrolled) return;
    const autoSound = this.dataset.tcmAutoSound === 'true';
    if (!autoSound) return;

    if (isActive) {
      document.dispatchEvent(new CustomEvent('video-section:play', { detail: { source: this } }));
      if (!this._userToggledSound && this._audioUnlocked) {
        this._video.volume = this._vol;
        this._updateSoundIcons(false);
      }
      this._video.play().catch(() => { });
    } else {
      if (!this._userToggledSound) {
        if (this._audioUnlocked) {
          this._video.volume = 0;
        } else {
          this._video.muted = true;
        }
        this._updateSoundIcons(true);
      }
      this._video.pause();
    }
  }

  _initStickyReveal() {
    this._wrap.style.removeProperty('--tcm-text-opacity');
    this._wrap.style.removeProperty('--tcm-text-y');
    if (this._cta) {
      this._cta.style.removeProperty('--tcm-cta-opacity');
      this._cta.style.removeProperty('--tcm-cta-y');
    }

    const lane = this.parentElement || this;
    if (typeof gsap === 'undefined' || !gsap.registerPlugin) return this._initNaturalReveal(false);

    this._soundTrigger = ScrollTrigger.create({
      trigger: lane,
      start: 'top 50%',
      end: 'bottom 50%',
      onEnter: () => this._handleStickySound(true),
      onLeave: () => this._handleStickySound(false),
      onEnterBack: () => this._handleStickySound(true),
      onLeaveBack: () => this._handleStickySound(false)
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: lane,
        start: 'top 15%',
        end: 'top 60%',
        scrub: 1,
        invalidateOnRefresh: true
      }
    });

    tl.to(this._wrap, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'none'
    }, 0);

    if (this._cta) {
      tl.to(this._cta, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'none'
      }, 0.2);
    }

    this._gsapTimeline = tl;
  }

  _initNaturalReveal(isMobile) {
    this._wrap.style.removeProperty('--tcm-text-opacity');
    this._wrap.style.removeProperty('--tcm-text-y');
    if (this._cta) {
      this._cta.style.removeProperty('--tcm-cta-opacity');
      this._cta.style.removeProperty('--tcm-cta-y');
    }

    if (typeof gsap === 'undefined' || !gsap.registerPlugin) {
      gsap.set(this._wrap, { opacity: 1, y: 0 });
      if (this._cta) gsap.set(this._cta, { opacity: 1, y: 0 });
      return;
    }

    const startVal = isMobile ? 'top 85%' : 'top 60%';
    const endVal = isMobile ? 'top 40%' : 'top 20%';

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: startVal,
        end: endVal,
        scrub: 1,
        invalidateOnRefresh: true
      }
    });

    tl.fromTo(this._wrap,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 1, ease: 'none' },
      0
    );

    if (this._cta) {
      tl.fromTo(this._cta,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: 'none' },
        0.2
      );
    }

    this._gsapTimeline = tl;
  }

  _initVideo() {
    const wrap = this.querySelector('[data-tcm-video-wrap]');
    if (!wrap) return;

    this._video = wrap.querySelector('video');
    this._soundBtn = wrap.querySelector('[data-tcm-sound]');

    if (!this._video) return;

    const autoSound = this.dataset.tcmAutoSound === 'true';
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this._vol = isIOS ? 0.1 : parseInt(this.dataset.volume || '50', 10) / 100;

    // Ensure video starts muted and paused — JS controls playback on viewport entry
    this._video.muted = true;
    this._video.pause();

    this._updateSoundIcons(true);

    if (this._soundBtn) {
      this._soundBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._userToggledSound = true;
        if (this._audioUnlocked) {
          const isSilent = this._video.volume === 0;
          this._video.volume = isSilent ? this._vol : 0;
          this._updateSoundIcons(!isSilent);
        } else {
          this._video.muted = !this._video.muted;
          this._updateSoundIcons(this._video.muted);
        }
      });
    }

    const isSticky = this.dataset.tcmMode === 'sticky';

    // Wait for real scroll before allowing playback — prevents playing on page load
    // even if the section is already visible in the viewport.
    this._onFirstScroll = () => {
      this._scrolled = true;
      window.removeEventListener('scroll', this._onFirstScroll);

      // If section is already in viewport, start playback now
      if (this._isInView && this._video) {
        document.dispatchEvent(new CustomEvent('video-section:play', { detail: { source: this } }));
        this._video.play().catch(() => {});
        if (autoSound && !isSticky && !this._userToggledSound && this._audioUnlocked) {
          this._video.volume = this._vol;
          this._updateSoundIcons(false);
        }
      }
    };
    window.addEventListener('scroll', this._onFirstScroll, { passive: true });

    // Gesture unlock: on first user gesture, unlock audio silently.
    // We play() with muted=false + volume=0 so iOS grants audio permission.
    // We restore the previous play state afterwards — observer controls playback.
    this._gestureUnlockHandler = () => {
      if (!this._video || this._audioUnlocked || this._userToggledSound) return;
      if (!autoSound) return;

      const wasPaused = this._video.paused;
      this._video.muted = false;
      this._video.volume = 0;
      const playPromise = this._video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          this._audioUnlocked = true;
          // Restore previous state — don't start video, observer handles that
          if (wasPaused) this._video.pause();
        }).catch(() => {
          this._video.muted = true;
        });
      }
    };

    document.addEventListener('touchstart', this._gestureUnlockHandler, { passive: true });
    document.addEventListener('touchend', this._gestureUnlockHandler, { passive: true });
    document.addEventListener('click', this._gestureUnlockHandler, { passive: true });

    // IntersectionObserver: play on enter, pause on exit
    // Only reacts after the user has scrolled (skips initial load callback)
    this._videoObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      this._isInView = entry.isIntersecting;

      // Skip if the user hasn't scrolled yet (initial page load)
      if (!this._scrolled) return;

      if (entry.isIntersecting) {
        document.dispatchEvent(new CustomEvent('video-section:play', { detail: { source: this } }));
        this._video.play().catch(() => { });

        if (autoSound && !isSticky && !this._userToggledSound) {
          if (this._audioUnlocked) {
            this._video.volume = this._vol;
            this._updateSoundIcons(false);
          }
        }
      } else {
        this._video.pause();

        if (autoSound && !isSticky && !this._userToggledSound) {
          if (this._audioUnlocked) {
            this._video.volume = 0;
          } else {
            this._video.muted = true;
          }
          this._updateSoundIcons(true);
        }
      }
    }, { threshold: 0.3 });

    this._videoObserver.observe(wrap);
  }

  _pauseAndSilence() {
    if (!this._video) return;
    this._video.pause();
    if (this._audioUnlocked) {
      this._video.volume = 0;
    } else {
      this._video.muted = true;
    }
    this._updateSoundIcons(true);
  }

  _updateSoundIcons(isMuted) {
    if (!this._soundBtn) return;
    const off = this._soundBtn.querySelector('.c-tcm__sound-icon--off');
    const on = this._soundBtn.querySelector('.c-tcm__sound-icon--on');
    if (off) off.style.display = isMuted ? 'block' : 'none';
    if (on) on.style.display = isMuted ? 'none' : 'block';
  }

  _teardown() {
    if (this._gsapTimeline) {
      this._gsapTimeline.scrollTrigger?.kill();
      this._gsapTimeline.kill();
      this._gsapTimeline = null;
    }
    if (this._soundTrigger) {
      this._soundTrigger.kill();
      this._soundTrigger = null;
    }
    if (this._videoObserver) {
      this._videoObserver.disconnect();
      this._videoObserver = null;
    }
    if (this._gestureUnlockHandler) {
      document.removeEventListener('touchstart', this._gestureUnlockHandler);
      document.removeEventListener('touchend', this._gestureUnlockHandler);
      document.removeEventListener('click', this._gestureUnlockHandler);
      this._gestureUnlockHandler = null;
    }
    if (this._onFirstScroll) {
      window.removeEventListener('scroll', this._onFirstScroll);
      this._onFirstScroll = null;
    }
    if (this._onExternalPlay) {
      document.removeEventListener('video-section:play', this._onExternalPlay);
      this._onExternalPlay = null;
    }
  }
}

if (!customElements.get('two-col-media')) {
  customElements.define('two-col-media', TwoColMedia);
}